import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text, Link, Flex} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'

const PolicyLink = ({children, href, testId}) => {
    return (
        <Link href={href} data-testid={testId} textDecoration="underline" isExternal={false}>
            {children}
        </Link>
    )
}

const policyLinks = {
    cancellationPolicy: (
        <PolicyLink href="/faq.html" testId="order-details-policy-cancellation">
            <FormattedMessage
                defaultMessage="FAQ"
                id="account_order_detail.status.cancellation_policy"
            />
        </PolicyLink>
    ),
    shippingLink: (
        <PolicyLink href="/shipping-policy.html" testId="order-details-policy-shipping">
            <FormattedMessage
                defaultMessage="Shipping and Delivery Policy"
                id="account_order_detail.status.shipping_and_delivery_policy"
            />
        </PolicyLink>
    ),
    returnPolicy: (
        <PolicyLink href="/return-policy.html" testId="order-details-policy-return">
            <FormattedMessage
                defaultMessage="Return Policy"
                id="account_order_detail.status.return_policy"
            />
        </PolicyLink>
    )
}

const DESCRIPTION_MESSAGES = {
    received: {
        id: 'account_order_detail.status.order_received',
        defaultMessage:
            "You can cancel your order or update the shipping address before it's processed. Don't worry, you won't be charged. Questions? See our {cancellationPolicy}.",
        values: {cancellationPolicy: policyLinks.cancellationPolicy}
    },
    receivedCancelOnly: {
        id: 'account_order_detail.status.order_received_cancel_only',
        defaultMessage:
            "You can cancel your order before it's processed. Don't worry, you won't be charged. Questions? See our {cancellationPolicy}.",
        values: {cancellationPolicy: policyLinks.cancellationPolicy}
    },
    receivedUpdateShippingOnly: {
        id: 'account_order_detail.status.order_received_update_shipping_only',
        defaultMessage:
            'You can update the shipping address before your order is processed. Questions? See our {cancellationPolicy}.',
        values: {cancellationPolicy: policyLinks.cancellationPolicy}
    },
    processing: {
        id: 'account_order_detail.status.order_processing',
        defaultMessage:
            'At this point, it cannot be cancelled or changed. Questions? See our {cancellationPolicy}.',
        values: {cancellationPolicy: policyLinks.cancellationPolicy}
    },
    shipped: {
        id: 'account_order_detail.status.order_shipped',
        defaultMessage:
            'Your order has left our facility and is on its way to you! At this point, it cannot be cancelled or changed. Questions? See our {shippingAndDeliveryPolicy}.',
        values: {
            shippingAndDeliveryPolicy: policyLinks.shippingLink,
        }
    },
    partiallyShipped: {
        id: 'account_order_detail.status.order_partially_shipped',
        defaultMessage:
            'Part of your order has left our facility and is on its way to you! At this point, it cannot be cancelled or changed. Questions? See our {shippingAndDeliveryPolicy}.',
        values: {
            shippingAndDeliveryPolicy: policyLinks.shippingLink,
        }
    },
    delivered: {
        id: 'account_order_detail.status.order_delivered',
        defaultMessage:
            "If you need to make a return, select the items you’d like to send back and print your return label. Questions? See our {returnPolicy}.",
        values: {returnPolicy: policyLinks.returnPolicy}
    },
    partiallyDelivered: {
        id: 'account_order_detail.status.order_partially_delivered',
        defaultMessage:
            "If you need to make a return, select the items you’d like to send back and print your return label. Questions? See our {returnPolicy}.",
        values: {returnPolicy: policyLinks.returnPolicy}
    },
    partiallyCancelled: {
        id: 'account_order_detail.status.order_partially_cancelled',
        defaultMessage:
            "If you need to make a return, select the items you’d like to send back and print your return label. Questions? See our {returnPolicy}.",
        values: {returnPolicy: policyLinks.returnPolicy}
    },
    deliveredFinalSale: {
        id: 'account_order_detail.status.order_delivered_final_sale',
        defaultMessage:
            'Your package has been delivered and this purchase is Final Sale. At this stage, it can no longer be returned. Questions? See {returnPolicy}.',
        values: {returnPolicy: policyLinks.returnPolicy}
    },
    deliveredReturnClosed: {
        id: 'account_order_detail.status.order_delivered_return_closed',
        defaultMessage:
            'Your order was delivered, but the return window is now closed. Unfortunately, at this point, you can no longer make a return. Questions? See our {returnPolicy}.',
        values: {returnPolicy: policyLinks.returnPolicy}
    },
    cancelled: {
        id: 'account_order_detail.status.order_partially_cancelled_by_system',
        defaultMessage:
            'Unfortunately, this order could not be shipped. The pending charge will be removed from your payment method in a few business days, depending on your bank.'
    },
    partiallyCancelledByCustomer: {
        id: 'account_order_detail.action.text.partially_cancelled_by_customer',
        defaultMessage:
            'Unfortunately, part of your order could not be shipped. The pending charge will be removed from your payment method in a few business days, depending on your bank.',
        values: {cancellationPolicy: policyLinks.cancellationPolicy}
    },
    refunded: {
        id: 'account_order_detail.action.text.refunded',
        defaultMessage:
            'Your refund may take a few business days to appear, depending on your bank. Questions? See our {returnPolicy}.',
        values: {returnPolicy: policyLinks.returnPolicy}
    },
    partiallyReturned: {
        id: 'account_order_detail.action.text.partially_returned',
        defaultMessage:
            'Your refund may take a few business days to appear, depending on your bank. Questions? See our {returnPolicy}.',
        values: {returnPolicy: policyLinks.returnPolicy}
    },
    pendingReturn: {
        id: 'account_order_detail.action.text.pending_return',
        defaultMessage:
            "We'll process your refund as soon as we receive and inspect your item(s). Questions? See our {returnPolicy}.",
        values: {returnPolicy: policyLinks.returnPolicy}
    },
    pickedUp: {
        id: 'account_order_detail.status.order_delivered',
        defaultMessage:
            'Select the items you’d like to send back and print your return label when you’re ready. Questions? See our {returnPolicy}.',
        values: {returnPolicy: policyLinks.returnPolicy}
    },
    bopisProcessing: {
        id: 'account_order_detail.status.order_pick_up_instore_processing',
        defaultMessage:
            'Your order is being prepared and will be ready for pick-up in 2 hours. At this point, it cannot be cancelled or changed. Questions? See our {cancellationPolicy}.',
        values: {cancellationPolicy: policyLinks.cancellationPolicy}
    }
}

const getDescriptionContent = (descriptionKey) => {
    if (descriptionKey === 'partiallyCancelledBySystem') {
        return (
            <Flex direction="column" gap={4}>
                <Text>
                    <FormattedMessage
                        id="account_order_detail.status.order_partially_cancelled_by_system"
                        defaultMessage="The pending charge will be removed from your payment method in a few business days, depending on your bank. "
                    />
                </Text>

                <Text>
                    <FormattedMessage {...DESCRIPTION_MESSAGES.partiallyCancelled} />
                </Text>
            </Flex>
        )
    }
    
    const message = DESCRIPTION_MESSAGES[descriptionKey]

    return message ? <FormattedMessage {...message} /> : null
}

export const DSSActionDescription = ({children, styles}) => {
    return (
        <Box
            data-testid="order-details-description"
            {...styles.descriptionText}
            width={{base: '95%', xl: '458px'}}
        >
            {children}
        </Box>
    )
}

const DSSActionDetails = ({descriptionKey, styles}) => {
    return (
        <DSSActionDescription styles={styles}>
            {getDescriptionContent(descriptionKey)}
        </DSSActionDescription>
    )
}

PolicyLink.propTypes = {
    children: PropTypes.node.isRequired,
    href: PropTypes.string.isRequired,
    testId: PropTypes.string.isRequired
}

DSSActionDescription.propTypes = {
    children: PropTypes.node,
    styles: PropTypes.object.isRequired
}

DSSActionDetails.propTypes = {
    descriptionKey: PropTypes.string,
    styles: PropTypes.object.isRequired
}

export default DSSActionDetails
